<script setup lang="ts">
import { i18n } from '@/i18n';
import { onMounted, computed, ref, watch, nextTick } from 'vue';
import { storeToRefs } from 'pinia';
import { useUnmatchedOrderStore } from './useUnmatchedOrderStore';
import TextButton from '@/components/input/Btn/TextButton.vue';
import SvgLoader from '@/utils/SvgLoader.vue';
import EzTable from '@/corePagesModule/components/ezTable/EzTable.vue';
import EzTableRow from '@/corePagesModule/components/ezTable/EzTableRow.vue';
import EzHeader from '@/corePagesModule/components/ezTable/EzHeader.vue';
import EzCell from '@/corePagesModule/components/ezTable/EzCell.vue';
import MatchConfirmModal from '../components/MatchConfirmModal.vue';
import FilterModalButton from '@/corePagesModule/filterModal/FilterModalButton.vue';
import Pagination from '@/corePagesModule/components/Pagination.vue';
import RegisterOrderBusinessModal from '../components/RegisterOrderBusinessModal.vue';
import { DateService } from '@/coreDomainsModule/services/dateService';
import { showInfoModal } from '@/corePagesModule/components/InfoModal/InfoModalManager';
import { ERROR_REASON_LABELS, TABLE_HEADERS } from '../constants';
import { OrderBusinessForRegister } from '../domains/entities/UnmatchedOrderItem';
import OrderStatusBadge from '@/corePagesModule/components/OrderStatusBadge.vue';
import BulkRegisterModal from '../components/BulkRegisterModal.vue';
import TableHeaderModifyButton from '@/corePagesModule/components/TableHeaderModifyButton.vue';
import { useTableHeader } from '@/corePagesModule/composables/useTableHeader';

const t = i18n.t;
const store = useUnmatchedOrderStore();
const { state } = storeToRefs(store);
const {
  init,
  toggleCheck,
  toggleCheckAll,
  onAppliedFilterModal,
  onChangedPaging,
  openMatchModal,
  closeMatchModal,
  matchChecked,
  registerOrderBusiness,
  deleteChecked,
} = store;

const defaultHeaderKeys = TABLE_HEADERS.map((v) => v.key);

const { headerList, updateDisplayHeader } = useTableHeader(defaultHeaderKeys, 'unmatchedOrder');

const errorReasonTableCellRef = ref<InstanceType<typeof EzCell>[]>([]);

const isAllChecked = computed(() => state.value.items.length > 0 && state.value.checkedIds.length === state.value.items.length);
const hasChecked = computed(() => state.value.checkedIds.length > 0);
const hasSingleChecked = computed(() => state.value.checkedIds.length === 1);
const checkedItems = computed(() => state.value.items.filter((item) => state.value.checkedIds.includes(item.unmatchedOrderId)));

const displayHeader = computed(() => TABLE_HEADERS.filter((h) => headerList.value.includes(h.key)));
const gridTemplateColumns = computed(() => displayHeader.value.map((h) => h.width));

const modifiableHeaderList = computed(() => {
  const headerListValue = headerList.value;
  const excludedKeys = new Set(['checkbox', 'headerModify']);

  return defaultHeaderKeys
    .filter((key) => !excludedKeys.has(key))
    .map((key) => {
      let text = TABLE_HEADERS.find((v) => v.key === key)?.label;
      if (!text) {
        return { key, text: 'ERROR', isShown: headerListValue.includes(key) };
      }
      return { key, text, isShown: headerListValue.includes(key) };
    });
});

/**
 * 新規登録ボタンを押した際の処理
 */
const onClickRegisterButton = () => {
  if (hasSingleChecked.value) {
    state.value.registerModalState = true;
  } else if (hasChecked.value) {
    state.value.bulkRegisterModalState = true;
  }
  return;
};

/**
 * 登録モーダルを閉じる処理
 * @param isNext - 次へ進んだか
 * @param info - 登録情報
 */
const onCloseRegisterOrderBusinessModal = async (isNext?: boolean, info?: OrderBusinessForRegister) => {
  state.value.registerModalState = false;
  if (isNext) {
    if (!info) {
      showInfoModal({
        modalTitle: t('エラーが発生しました'),
        modalDescription: t('発注登録中にエラーが発生しました。再度お試しください。'),
        backButtonLabel: t('OK'),
        isDisabledNextButton: true,
        icon: 'error',
      });
      return;
    }
    registerOrderBusiness(info);
  }
};

/**
 * ハイライトされているかどうか
 * @param businessId - 取引ID
 */
const getIsChecked = (businessId: string) => state.value.checkedIds.includes(businessId);

onMounted(() => {
  init();
});

watch(
  () => i18n.locale,
  async () => {
    await nextTick();
    if (errorReasonTableCellRef.value) errorReasonTableCellRef.value.forEach((r) => r.notifyTooltipContentChange());
  }
);
</script>

<template>
  <!-- 未照合・差異一覧ページ -->
  <div class="unmatched-order">
    <!-- タイトル -->
    <div class="unmatched-order__title unmatched-order-title">
      <span class="unmatched-order-title__label">{{ t('未照合・差異一覧') }}</span>
      <div class="unmatched-order-title__nav unmatched-order-nav">
        <RouterLink class="unmatched-order-nav__label" to="/firm-order">{{ t('発注一覧ページに切り替え') }}</RouterLink>
        <SvgLoader class="unmatched-order-nav__icon" name="orderSwitchIcon" />
      </div>
    </div>

    <div class="unmatched-order__contains">
      <div class="unmatched-order__head-area">
        <div class="unmatched-order__head-area--left">
          <FilterModalButton :filter-modal-type="'unmatchedOrder'" @apply="onAppliedFilterModal" />
          <TextButton
            :button-style="'btn-primary'"
            :size="'btn-md'"
            :disabled="!hasSingleChecked"
            :on-click="openMatchModal"
            class="bulk-action-button-container__button"
          >
            {{ t('突合ボタン') }}
          </TextButton>
          <TextButton
            :button-style="'btn-primary'"
            :size="'btn-md'"
            :disabled="!hasChecked"
            :on-click="onClickRegisterButton"
            class="bulk-action-button-container__button"
          >
            {{ t('新規登録ボタン') }}
          </TextButton>
          <TextButton
            :button-style="'btn-error'"
            :size="'btn-md'"
            :disabled="!hasChecked"
            :on-click="deleteChecked"
            class="bulk-action-button-container__button"
          >
            {{ t('削除ボタン') }}
          </TextButton>
        </div>
        <div class="unmatched-order__head-area--right">
          <Pagination
            :paging-options="state.pagingOption"
            :is-loading="state.loadingStatus.type === 'loading'"
            @on-change-page="onChangedPaging"
          />
        </div>
      </div>

      <EzTable
        :grid-template-columns="gridTemplateColumns"
        sticky-header
        :loading-status="state.loadingStatus"
        max-height="calc(100vh - 200px)"
        class="base-table-wrapper"
      >
        <template #header>
          <EzHeader force-center class="table-checkbox">
            <input
              type="checkbox"
              class="unmatched-checkbox"
              :checked="isAllChecked"
              :indeterminate="hasChecked && !isAllChecked"
              @change="toggleCheckAll()"
            />
          </EzHeader>
          <EzHeader v-for="header in displayHeader.slice(1, -1)" :key="`header:${header.key}`">
            {{ t(header.label) }}
          </EzHeader>
          <EzHeader sticky="right">
            <TableHeaderModifyButton :modifiable-header-list="modifiableHeaderList" @toggle="updateDisplayHeader" />
          </EzHeader>
        </template>
        <template #body>
          <EzTableRow
            v-for="(item, index) in state.items"
            :key="item.unmatchedOrderId"
            :grid-template-columns="gridTemplateColumns"
            class="unmatched-row"
          >
            <EzCell force-center :is-highlighted="getIsChecked(item.unmatchedOrderId)" class="table-checkbox">
              <input
                type="checkbox"
                class="unmatched-checkbox"
                :checked="getIsChecked(item.unmatchedOrderId)"
                @change="toggleCheck(item.unmatchedOrderId)"
              />
            </EzCell>
            <template v-for="header in displayHeader" :key="`row:${header.key}:${index}`">
              <EzCell v-if="header.key === 'orderStatus'" force-center :is-highlighted="getIsChecked(item.unmatchedOrderId)">
                <span class="order-status">
                  <OrderStatusBadge :order-status-str="item.orderStatus" />
                </span>
              </EzCell>
              <EzCell
                v-else-if="header.key === 'errorReason'"
                ref="errorReasonTableCellRef"
                :is-highlighted="getIsChecked(item.unmatchedOrderId)"
              >
                <span class="error-reason">{{ t(ERROR_REASON_LABELS[item.errorReason] ?? ERROR_REASON_LABELS.unknown) }}</span>
              </EzCell>
              <EzCell v-else-if="header.type === 'num'" is-number :is-highlighted="getIsChecked(item.unmatchedOrderId)">
                {{ item[header.key]?.toLocaleString() ?? '' }}
              </EzCell>
              <EzCell v-else-if="header.type === 'date'" :is-highlighted="getIsChecked(item.unmatchedOrderId)">
                {{ item[header.key] ? DateService.convertMSUnixTimeToYYYYMMDDSlash(item[header.key]) : '' }}
              </EzCell>
              <EzCell v-else-if="header.type === 'str'" :is-highlighted="getIsChecked(item.unmatchedOrderId)">
                {{ item[header.key] ?? '' }}
              </EzCell>
            </template>
            <!-- ヘッダー編集ボタン -->
            <EzCell :is-highlighted="getIsChecked(item.unmatchedOrderId)" />
          </EzTableRow>
        </template>
      </EzTable>
    </div>

    <!-- 突合モーダル -->
    <MatchConfirmModal
      :modal-state="state.matchModalState"
      :checked-items="checkedItems"
      @canceled="closeMatchModal"
      @confirm="matchChecked"
    />

    <RegisterOrderBusinessModal
      :modal-state="state.registerModalState"
      :item="checkedItems[0]"
      @close="onCloseRegisterOrderBusinessModal"
    />

    <BulkRegisterModal />
  </div>
</template>

<style lang="scss" scoped>
.unmatched-order {
  display: grid;
  gap: 16px;
  margin: 16px;

  &__title {
    font-family: Noto Sans JP;
    font-size: 22px;
    font-weight: 700;
    line-height: 32px;
    letter-spacing: 0em;
    text-align: left;
    color: $textDefault;
  }

  &__contains {
    display: grid;
    gap: 8px;
    &--loading {
      align-self: center;
      height: 100%;
    }
  }

  &__head-area {
    display: grid;
    grid-auto-flow: column;
    justify-content: space-between;
    align-items: center;
    &--left {
      display: grid;
      grid-auto-flow: column;
      align-items: center;
      gap: 16px;
    }
    &--right {
      display: grid;
      grid-auto-flow: column;
      align-items: center;
      gap: 16px;
    }
  }

  &__count {
    font-family: Noto Sans JP;
    font-size: 14px;
    font-weight: 400;
    color: $textSecondary;
  }
}

.unmatched-order-title {
  display: grid;
  grid-template-columns: repeat(2, fit-content(100%));
  align-items: center;
  column-gap: 48px;

  &__label {
    font-size: 22px;
    font-weight: 700;
    line-height: 28px;
  }
}

.unmatched-order-nav {
  display: grid;
  height: 100%;
  grid-template-columns: auto auto;
  align-items: baseline;
  justify-items: center;
  column-gap: 4px;
  pointer-events: none;
  cursor: pointer;
  padding: 0px 12px 4px 12px;

  &:hover {
    background: $semanticPrimarySubdued;
    transition: 0.3ms;
    border-radius: 90px;
  }

  &__label {
    pointer-events: auto;
    font-size: 16px;
    font-weight: 700;
    line-height: 16px;
    color: $semanticPrimary;
  }

  &__icon {
    pointer-events: auto;
    width: 20px;
    height: 20px;
    align-content: end;
  }
}

.bulk-action-button-container {
  &__button {
    font-size: 14px;
  }
}

.unmatched-checkbox {
  width: 16px;
  height: 16px;
  border-radius: 2px;
  border: 1px solid $borderSecondary;
  cursor: pointer;
  accent-color: $semanticPrimary;
  vertical-align: middle;

  &:hover {
    border-color: $semanticPrimary;
  }
}

.unmatched-order__contains {
  :deep(.ez-table .table__header-container) {
    width: 100%;
  }

  :deep(.table__header) {
    min-height: 36px;
  }

  :deep(.table__header__content) {
    line-height: 36px;
  }
}

.unmatched-row {
  // 発注テーブルのTableCell.vueと同じスタイルに合わせる
  :deep(.table__cell) {
    border-bottom: 1px solid $surfaceSecondary;
  }

  :deep(.table__cell__content) {
    padding-top: 4px;
    padding-bottom: 4px;
  }
}

.error-reason {
  color: $semanticError;
  font-family: Noto Sans JP;
  font-size: 12px;
  font-weight: 500;
  line-height: 12px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.table-checkbox {
  position: sticky;
  left: 0;
  z-index: 1;
}
</style>
